import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './css/AddRestaurant.css'; // Ensure this path is correct
import axiosRestaurantInstance from './AxiosRestaurantInstance';
import {
  formatFssaiDisplay,
  sanitizeFssaiInput,
  validateFssaiNumber,
} from './formatDisplay';
import RestaurantServicesPicker from './RestaurantServicesPicker';
import {
  buildServicesPayload,
  emptyServiceSelections,
  validateServicesSelection,
} from './restaurantServiceConstants';
import { canManageRestaurantServices } from '../utils/eatsAppAuth';

const ADDRESS_SEARCH_DEBOUNCE_MS = 350;

const AddRestaurant = ({ onClose, createdById, onSuccess, embedded = false }) => {
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem('user'));

    const [restaurantName, setRestaurantName] = useState('');
    const [cuisineType, setCuisineType] = useState('');
    const [fssaiNumber, setFssaiNumber] = useState('');
    const [contactNo, setContactNo] = useState('');
    const [email, setEmail] = useState('');
    const [notes, setNotes] = useState('');
    const [status, setStatus] = useState('ACTIVE');
    const [isDefault, setIsDefault] = useState(true);
    const [addressLine1, setAddressLine1] = useState('');
    const [addressLine2, setAddressLine2] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [country, setCountry] = useState('');
    const [longitude, setLongitude] = useState('');
    const [latitude, setLatitude] = useState('');
    const [addressSearch, setAddressSearch] = useState('');
    const [addressSuggestions, setAddressSuggestions] = useState([]);
    const [addressSearchLoading, setAddressSearchLoading] = useState(false);
    const [addressSelected, setAddressSelected] = useState(false);
    const [addressSearchMessage, setAddressSearchMessage] = useState('');
    const addressSearchTimeoutRef = useRef(null);

    const clearSelectedAddress = () => {
        setAddressSelected(false);
        setAddressLine1('');
        setPostalCode('');
        setCity('');
        setState('');
        setCountry('');
        setLatitude('');
        setLongitude('');
    };

    const [isHomeDeliveryAvailable, setIsHomeDeliveryAvailable] = useState(false);
    const [selectedIngredientIds, setSelectedIngredientIds] = useState([]);
    const [serviceSelections, setServiceSelections] = useState(emptyServiceSelections);
    const showServicesPicker = canManageRestaurantServices(user);

    // Address search as user types in the separate Search Address box (debounced)
    const handleAddressSearchChange = (e) => {
        const value = e.target.value;
        setAddressSearch(value);
        setAddressSuggestions([]);
        setAddressSearchMessage('');
        if (addressSelected) clearSelectedAddress();

        if (addressSearchTimeoutRef.current) {
            clearTimeout(addressSearchTimeoutRef.current);
            addressSearchTimeoutRef.current = null;
        }

        if (value.trim().length < 2) return;

        addressSearchTimeoutRef.current = setTimeout(async () => {
            addressSearchTimeoutRef.current = null;
            try {
                setAddressSearchLoading(true);
                const response = await axiosRestaurantInstance.get(`/address/suggest`, {
                    params: { address: value.trim() },
                });
                const list = response.data?.suggestions || response.data?.predictions || [];
                const rows = Array.isArray(list) ? list : [];
                setAddressSuggestions(rows);
                setAddressSearchMessage(rows.length ? '' : 'No matching addresses. Try a different search.');
            } catch (error) {
                console.error('Error fetching address suggestions:', error.response ? error.response.data : error.message);
                setAddressSuggestions([]);
                setAddressSearchMessage(
                    error?.response?.status === 404
                        ? 'No matching addresses. Try a different search.'
                        : error?.response?.data?.error || 'Address search failed. Please try again.'
                );
            } finally {
                setAddressSearchLoading(false);
            }
        }, ADDRESS_SEARCH_DEBOUNCE_MS);
    };

    // Handle Address Selection
    const handleAddressSelect = async (placeId, label = '') => {
        try {
            // Use organization API for address details (same as organization form)
            const response = await axiosRestaurantInstance.get(`/address/suggest/details`, {
                params: { placeId },
            });

            const placeDetails = response.data;

            if (!placeDetails) {
                alert('Address details not found');
                return;
            }

            // Automatically fill in the form fields with the fetched address details
            setAddressLine1(placeDetails.addressLine1 || '');
            setAddressLine2(placeDetails.addressLine2 || '');
            setPostalCode(placeDetails.postalCode || '');
            setCity(placeDetails.city || '');
            setState(placeDetails.state || '');
            setCountry(placeDetails.country || '');
            setLatitude(placeDetails.latitude ?? placeDetails.lat ?? '');
            setLongitude(placeDetails.longitude ?? placeDetails.lon ?? '');
            setAddressSuggestions([]);
            setAddressSearch(label || placeDetails.formattedAddress || placeDetails.addressLine1 || '');
            setAddressSelected(true);
        } catch (error) {
            console.error('Error fetching place details:', error.response ? error.response.data : error.message);
            alert('Could not load that address. Please pick another suggestion.');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!addressSelected) {
            alert('Search and select the restaurant address from the suggestions.');
            return;
        }
        if (!(city || '').trim()) {
            alert('The selected address has no city. Please pick a more specific address.');
            return;
        }

        if (showServicesPicker) {
            const servicesError = validateServicesSelection(serviceSelections);
            if (servicesError) {
                alert(servicesError);
                return;
            }
        }

        const homeDelivery = isHomeDeliveryAvailable;
        const contactDigits = String(contactNo || '').replace(/\D/g, '');
        if (contactDigits.length !== 10) {
            alert('Please enter a valid 10-digit business phone.');
            return;
        }
        const contactNoNum = parseInt(contactDigits, 10);
        const isDefaultBoolean = Boolean(isDefault);
        const fssaiError = validateFssaiNumber(fssaiNumber, { required: true, label: 'FSSAI number' });
        if (fssaiError) {
            alert(fssaiError);
            return;
        }

        const latParsed =
            latitude !== '' && latitude != null ? parseFloat(String(latitude), 10) : NaN;
        const lonParsed =
            longitude !== '' && longitude != null ? parseFloat(String(longitude), 10) : NaN;
        if (!Number.isFinite(latParsed) || !Number.isFinite(lonParsed)) {
            alert('Latitude and longitude are required. Search and select an address to set them.');
            return;
        }

        // Payload aligned with POST /restaurant
        const effectiveCreatedById = createdById || user?.userId;
        if (!effectiveCreatedById) {
            alert('Created user is missing. Please create/select root user first.');
            return;
        }

        const restaurantData = {
            restaurantName: (restaurantName || '').trim(),
            contactNo: contactNoNum,
            email: (email || '').trim(),
            cuisineType: (cuisineType || '').trim() || undefined,
            fssaiNumber: sanitizeFssaiInput(fssaiNumber),
            notes: (notes || '').trim() || undefined,
            addressLine1: (addressLine1 || '').trim(),
            addressLine2: (addressLine2 || '').trim(),
            postalCode: (postalCode || '').trim(),
            city: (city || '').trim(),
            state: (state || '').trim(),
            country: (country || '').trim(),
            createdById: effectiveCreatedById,
            isDefault: isDefaultBoolean,
            homeDeliveryAvailable: homeDelivery,
            lat: latParsed,
            lon: lonParsed,
        };

        if (showServicesPicker) {
            const services = buildServicesPayload(serviceSelections);
            if (services.length > 0) {
                restaurantData.services = services;
            }
        }

        try {
            const token = localStorage.getItem('Token');
            const response = await axiosRestaurantInstance.post('/restaurant', restaurantData, {
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { Authorization: `Bearer ${token}` }),
                },
            });

            const ok = response.status === 201 || response.status === 200;
            const message = response.data?.message;
            if (ok) {
                alert(message || 'Restaurant added successfully');
                // Resetting the form fields
                setRestaurantName('');
                setCuisineType('');
                setContactNo('');
                setEmail('');
                setNotes('');
                setStatus('ACTIVE');
                setIsDefault('true');
                setAddressLine1('');
                setAddressLine2('');
                setAddressSearch('');
                setCity('');
                setIsHomeDeliveryAvailable(false);
                setState('');
                setPostalCode('');
                setCountry('');
                setLongitude('');
                setLatitude('');
                setAddressSelected(false);
                setServiceSelections(emptyServiceSelections());
                if (typeof onSuccess === 'function') {
                    onSuccess(response?.data || {});
                }
                // Close the form if onClose prop is provided
                if (onClose) {
                    onClose();
                }
            } else {
                alert('Failed to add restaurant');
            }
        } catch (error) {
            console.error('Error adding restaurant:', error.response ? error.response.data : error.message);
            alert(
                error?.response?.data?.message ||
                    error?.response?.data?.error ||
                    'An error occurred while adding the restaurant.'
            );
        }
    };

    const handleClose = () => {
        if (onClose) {
            onClose();
        } else {
            navigate('/home');
        }
    };

    return (
        <div className={`container1${embedded ? ' add-restaurant-embedded' : ''}`}>
            <div className="add-restaurant-container visible">
                <form onSubmit={handleSubmit}>
                    <div className="add-restaurant-form-row">
                        <div className="add-restaurant-form-group">
                            <label>Restaurant Name</label>
                            <input
                                type="text"
                                value={restaurantName}
                                onChange={(e) => setRestaurantName(e.target.value)}
                                placeholder="Enter restaurant name"
                                required
                            />
                        </div>
                        <div className="add-restaurant-form-group">
                            <label>Business Phone</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                maxLength={10}
                                value={contactNo}
                                onChange={(e) =>
                                    setContactNo(e.target.value.replace(/\D/g, '').slice(0, 10))
                                }
                                placeholder="Enter 10-digit business phone"
                                required
                            />
                        </div>
                    </div>

                    <div className="add-restaurant-form-row">
                        <div className="add-restaurant-form-group">
                            <label>Email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter email"
                                required
                            />
                        </div>
                        <div className="add-restaurant-form-group">
                            <label>Cuisine Type</label>
                            <input
                                type="text"
                                value={cuisineType}
                                onChange={(e) => setCuisineType(e.target.value)}
                                placeholder="Enter cuisine type"
                            />
                        </div>
                        <div className="add-restaurant-form-group">
                            <label>FSSAI Number</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                maxLength={17}
                                value={formatFssaiDisplay(fssaiNumber)}
                                onChange={(e) => setFssaiNumber(sanitizeFssaiInput(e.target.value))}
                                placeholder="Enter 14-digit FSSAI number"
                                required
                            />
                        </div>
                    </div>

                    <div className="add-restaurant-form-row">
                        <label className="add-restaurant-form-group add-restaurant-checkbox-group">
                            <span className="add-restaurant-checkbox-group-heading">Is Default</span>
                            <input
                                type="checkbox"
                                className="add-restaurant-checkbox-large"
                                checked={isDefault}
                                onChange={(e) => setIsDefault(e.target.checked)}
                            />
                        </label>
                        <label className="add-restaurant-form-group add-restaurant-checkbox-group">
                            <span className="add-restaurant-checkbox-group-heading">Home Delivery</span>
                            <input
                                type="checkbox"
                                className="add-restaurant-checkbox-large"
                                checked={isHomeDeliveryAvailable}
                                onChange={(e) => setIsHomeDeliveryAvailable(e.target.checked)}
                            />
                        </label>
                    </div>

                    <div className="add-restaurant-form-row">
                        <div className="add-restaurant-form-group full-width">
                            <label>Notes</label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Enter any notes"
                            />
                        </div>
                    </div>

                    {/* Address Section - separate Search Address box, then address fields */}
                    <h3>Address</h3>
                    <div className="add-restaurant-form-row add-restaurant-address-search-row">
                        <div className="add-restaurant-form-group add-restaurant-address-search-wrap">
                            <label>Search Address</label>
                            <input
                                type="text"
                                value={addressSearch}
                                onChange={handleAddressSearchChange}
                                placeholder="Type to search address..."
                                autoComplete="off"
                                className="add-restaurant-address-search-input"
                            />
                            {addressSearchLoading && (
                                <span className="add-restaurant-address-loading">Searching...</span>
                            )}
                            {!addressSearchLoading && addressSearchMessage && addressSuggestions.length === 0 ? (
                                <span className="add-restaurant-address-message">{addressSearchMessage}</span>
                            ) : null}
                            {addressSuggestions.length > 0 && (
                                <ul className="address-suggestions-dropdown">
                                    {addressSuggestions.map((suggestion, index) => {
                                        const placeId = suggestion.place_id || suggestion.placeId;
                                        const text = suggestion.description || suggestion.formatted_address || '';
                                        return (
                                            <li
                                                key={placeId || index}
                                                onClick={() => handleAddressSelect(placeId, text)}
                                            >
                                                {text}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </div>
                    <div className="add-restaurant-form-row">
                        <div className="add-restaurant-form-group">
                            <label>Address Line 1</label>
                            <input
                                type="text"
                                value={addressLine1}
                                placeholder="Filled from address search"
                                readOnly
                            />
                        </div>
                        <div className="add-restaurant-form-group">
                            <label>Address Line 2</label>
                            <input
                                type="text"
                                value={addressLine2}
                                onChange={(e) => setAddressLine2(e.target.value)}
                                placeholder="Flat, floor, landmark (optional)"
                            />
                        </div>
                        <div className="add-restaurant-form-group">
                            <label>Postal Code</label>
                            <input
                                type="text"
                                value={postalCode}
                                placeholder="Filled from address search"
                                readOnly
                            />
                        </div>
                    </div>

                    <div className="add-restaurant-form-row">
                        <div className="add-restaurant-form-group">
                            <label>City</label>
                            <input
                                type="text"
                                value={city}
                                placeholder="Filled from address search"
                                readOnly
                            />
                        </div>
                        <div className="add-restaurant-form-group">
                            <label>State</label>
                            <input
                                type="text"
                                value={state}
                                placeholder="Filled from address search"
                                readOnly
                            />
                        </div>
                        <div className="add-restaurant-form-group">
                            <label>Country</label>
                            <input
                                type="text"
                                value={country}
                                placeholder="Filled from address search"
                                readOnly
                            />
                        </div>
                    </div>

                    <div className="add-restaurant-form-row">
                        <div className="add-restaurant-coords-pair">
                            <div className="add-restaurant-form-group">
                                <label>Longitude</label>
                                <input
                                    type="text"
                                    value={longitude}
                                    onChange={(e) => setLongitude(e.target.value)}
                                    placeholder="Enter longitude"
                                    readOnly
                                />
                            </div>
                            <div className="add-restaurant-form-group">
                                <label>Latitude</label>
                                <input
                                    type="text"
                                    value={latitude}
                                    onChange={(e) => setLatitude(e.target.value)}
                                    placeholder="Enter latitude"
                                    readOnly
                                />
                            </div>
                        </div>
                    </div>

                    {showServicesPicker ? (
                        <RestaurantServicesPicker
                            selections={serviceSelections}
                            onChange={setServiceSelections}
                        />
                    ) : null}

                    <div className="add-restaurant-submit-button">
                        <button type="submit">Save</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddRestaurant;
